/**
 * Hook for drawing operations in canvas
 * Extracted from BaseCanvas.tsx to reduce component complexity
 */

import { useCallback, useEffect, useState } from 'react'
import { convertToSharp, extractMainCoordinates } from '@/utils/Curve.utils'
import { processSmoothPath } from '@/utils/SmoothPath.utils'
import type { ControlPoint, Drawing, PathStyle } from '@/types'
import type { BaseCanvasCallbacks, Player } from '../BaseCanvas.types'
import { distance } from '@/utils/Geometry.utils'
import { eventBus } from '@/services/EventBus'

interface EditingDrawingState {
	drawing: Drawing
	position: { x: number; y: number }
}

interface UseDrawingOperationsOptions {
	drawings: Drawing[]
	players: Player[]
	callbacks: BaseCanvasCallbacks
	onBeforeStateChange?: () => void
}

interface UseDrawingOperationsReturn {
	editingDrawing: EditingDrawingState | null
	setEditingDrawing: (editing: EditingDrawingState | null) => void
	handleDrawingSelectWithDialog: (id: string, position: { x: number; y: number }) => void
	handleDrawingStyleUpdate: (updates: Partial<PathStyle>) => void
	handleDeleteDrawing: (id: string) => void
	handleLinkDrawingToPlayer: (
		drawingId: string,
		pointId: string,
		playerId: string,
		newPlayerOverride?: Player,
	) => void
	handleUnlinkDrawing: (playerId: string) => void
}

// Helper to find neighbor point of a linked point
function findNeighborPoint(linkedDrawing: Drawing, linkedPointId: string): ControlPoint | null {
	for (const segment of linkedDrawing.segments) {
		const pointIndex = segment.pointIds.indexOf(linkedPointId)
		if (pointIndex === -1) continue
		if (pointIndex > 0) {
			const prevId = segment.pointIds[pointIndex - 1]
			return prevId ? (linkedDrawing.points[prevId] ?? null) : null
		}
		if (pointIndex < segment.pointIds.length - 1) {
			const nextId = segment.pointIds[pointIndex + 1]
			return nextId ? (linkedDrawing.points[nextId] ?? null) : null
		}
	}
	return null
}

// Helper to compute unlink target position
function computeUnlinkTarget(
	player: { x: number; y: number },
	neighborPoint: ControlPoint | null,
	unlinkDistanceFeet: number,
) {
	if (!neighborPoint) {
		return { x: player.x, y: player.y - unlinkDistanceFeet }
	}

	// Calculate direction along the drawing (from player toward neighbor)
	const dx = neighborPoint.x - player.x
	const dy = neighborPoint.y - player.y
	const dist = distance(player, neighborPoint)

	if (dist === 0) {
		return { x: player.x, y: player.y - unlinkDistanceFeet }
	}

	// Move unlink distance in that direction from player
	const unitX = dx / dist
	const unitY = dy / dist
	return {
		x: player.x + unitX * unlinkDistanceFeet,
		y: player.y + unitY * unlinkDistanceFeet,
	}
}

const UNLINK_DISTANCE_FEET = 3

/**
 * Manages drawing operations: select, style update, delete, and linking
 */
export function useDrawingOperations({
										 drawings,
										 players,
										 callbacks,
										 onBeforeStateChange,
									 }: UseDrawingOperationsOptions): UseDrawingOperationsReturn {
	const [editingDrawing, setEditingDrawing] = useState<EditingDrawingState | null>(null)

	useEffect(() => {
		function handleCloseAll() {
			setEditingDrawing(null)
		}
		eventBus.on('dialog:closeAll', handleCloseAll)
		return () => eventBus.off('dialog:closeAll', handleCloseAll)
	}, [])

	const handleDrawingSelectWithDialog = useCallback(
		(id: string, position: { x: number; y: number }) => {
			const drawing = drawings.find((d) => d.id === id)
			if (drawing) {
				setEditingDrawing({ drawing, position })
			}
		},
		[drawings],
	)

	const handleDrawingStyleUpdate = useCallback(
		(updates: Partial<PathStyle>) => {
			if (!editingDrawing) return

			onBeforeStateChange?.()

			const drawing = editingDrawing.drawing
			let updatedDrawing = {
				...drawing,
				style: { ...drawing.style, ...updates },
			}

			// If pathMode changed, convert geometry
			if (updates.pathMode && updates.pathMode !== drawing.style.pathMode) {
				if (updates.pathMode === 'curve') {
					// Convert to smooth using smooth pipeline
					const coords = extractMainCoordinates(drawing)
					const { points, segments } = processSmoothPath(coords)
					updatedDrawing = { ...updatedDrawing, points, segments }
				} else {
					// Convert to sharp using convertToSharp
					const { points, segments } = convertToSharp(drawing)
					updatedDrawing = { ...updatedDrawing, points, segments }
				}
			}

			const updatedDrawings = drawings.map((d) =>
				d.id === editingDrawing.drawing.id ? updatedDrawing : d,
			)
			callbacks.onDrawingsChange(updatedDrawings)
			setEditingDrawing({ ...editingDrawing, drawing: updatedDrawing })
		},
		[editingDrawing, drawings, callbacks, onBeforeStateChange],
	)

	const handleDeleteDrawing = useCallback(
		(id: string) => {
			onBeforeStateChange?.()
			callbacks.onDrawingDelete(id)
		},
		[callbacks, onBeforeStateChange],
	)

	const handleLinkDrawingToPlayer = useCallback(
		(
			drawingId: string,
			pointId: string,
			playerId: string,
			newPlayerOverride?: Player,
		) => {
			const existingLink = drawings.find((d) => d.playerId === playerId)
			if (existingLink) return

			// Use override if provided (for newly created players not yet in state)
			const player = newPlayerOverride || players.find((p) => p.id === playerId)
			if (!player) return

			onBeforeStateChange?.()

			const updatedDrawings = drawings.map((drawing) => {
				if (drawing.id !== drawingId) return drawing

				// Update the linked point in the shared pool
				const updatedPoints = {
					...drawing.points,
					[pointId]: {
						...drawing.points[pointId]!,
						x: player.x,
						y: player.y,
					},
				}

				return {
					...drawing,
					playerId,
					linkedPointId: pointId,
					points: updatedPoints,
				}
			})
			callbacks.onDrawingsChange(updatedDrawings)
		},
		[drawings, players, callbacks, onBeforeStateChange],
	)

	const handleUnlinkDrawing = useCallback(
		(playerId: string) => {
			const linkedDrawing = drawings.find((d) => d.playerId === playerId)
			if (!linkedDrawing || !linkedDrawing.linkedPointId) return

			const player = players.find((p) => p.id === playerId)
			if (!player) return

			onBeforeStateChange?.()

			// Capture linkedPointId to satisfy TypeScript narrowing
			const pointIdToUnlink = linkedDrawing.linkedPointId

			const neighborPoint = findNeighborPoint(linkedDrawing, pointIdToUnlink)
			const newPosition = computeUnlinkTarget(player, neighborPoint, UNLINK_DISTANCE_FEET)

			const updatedDrawings = drawings.map((drawing) => {
				if (drawing.id !== linkedDrawing.id) return drawing

				const existingPoint = drawing.points[pointIdToUnlink]
				if (!existingPoint) return drawing

				// Update the unlinked point in the shared pool
				const updatedPoints = {
					...drawing.points,
					[pointIdToUnlink]: {
						...existingPoint,
						x: newPosition.x,
						y: newPosition.y,
					},
				}

				return {
					...drawing,
					playerId: undefined,
					linkedPointId: undefined,
					points: updatedPoints,
				}
			})
			callbacks.onDrawingsChange(updatedDrawings)
		},
		[drawings, players, callbacks, onBeforeStateChange],
	)

	return {
		editingDrawing,
		setEditingDrawing,
		handleDrawingSelectWithDialog,
		handleDrawingStyleUpdate,
		handleDeleteDrawing,
		handleLinkDrawingToPlayer,
		handleUnlinkDrawing,
	}
}
